import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { ChevronDown, ArrowRight, ShieldCheck, CheckCircle2, FlaskConical, Settings, Award, Boxes, Search, Star, Droplet, Sun, Wind } from 'lucide-react';

export default function SkincarePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero Section */}
        <section className="relative pt-20 pb-32 px-6 lg:px-12 bg-gradient-to-br from-[#FFFDF9] to-[#F9F5EC] overflow-hidden" data-aos="fade-up">
          {/* Background Image / Overlay */}
          <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 opacity-20 lg:opacity-100 z-0 hidden md:block">
            <img src="https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=1200&auto=format&fit=crop" alt="Skincare Products" className="w-full h-full object-cover object-left mask-image-gradient-left" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F9F5EC] via-[#F9F5EC]/80 to-transparent"></div>
          </div>

          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="w-full lg:w-1/2 pt-10">
              <span className="inline-block py-1.5 px-4 bg-[#E6D5B8]/40 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded mb-4">
                MAKLON SKINCARE PREMIUM
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.15] text-[#302621]">
                Kembangkan Produk <br /> Skincare Berkualitas <br /> Bersama Kami
              </h1>
              <p className="text-base text-[#7A695B] mb-10 leading-relaxed max-w-md font-light">
                Mulai dari formulasi hingga produksi massal, kami membantu mewujudkan produk skincare yang aman, efektif, dan siap bersaing di pasar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1">
                  Konsultasi Produk <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="border-[#302621] text-[#302621] bg-transparent hover:bg-[#302621] hover:text-white px-8 py-6 text-base rounded-lg transition-transform hover:-translate-y-1">
                  Lihat Kategori
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Row */}
        <section className="relative -mt-16 px-6 lg:px-12 z-20" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { count: "750+", label: "Produk Diproduksi", icon: <FlaskConical /> },
                { count: "150+", label: "Brand Bermitra", icon: <Award /> },
                { count: "10+", label: "Tahun Pengalaman", icon: <Settings /> },
                { count: "95%", label: "Repeat Order", icon: <CheckCircle2 /> }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-xl shadow-[#302621]/5 border border-[#E6D5B8]/30 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-[#F9F5EC] flex items-center justify-center text-[#D4AF37] mb-4">
                    {stat.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-[#302621] mb-1">{stat.count}</h3>
                  <p className="text-xs text-[#7A695B] font-medium uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Kategori Produk */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold text-[#302621] mb-12 text-center">Kategori Produk Skincare</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Moisturizer", points: ["Melembapkan kulit", "Barrier repair", "Hydrating"], icon: <Droplet /> },
                { title: "Serum", points: ["Brightening", "Anti-Aging", "Acne Care"], icon: <Star /> },
                { title: "Facial Wash", points: ["Gel Cleanser", "Foam Cleanser", "Gentle Wash"], icon: <Wind /> },
                { title: "Sunscreen", points: ["UV Protection", "SPF 30 / SPF 50", "Lightweight"], icon: <Sun /> },
                { title: "Day Cream", points: ["Perlindungan harian", "Mencerahkan", "Tone up effect"], icon: <Sun /> },
                { title: "Lain-lain", points: ["Toner", "Essence", "Sleeping Mask"], icon: <FlaskConical /> }
              ].map((cat, idx) => (
                <div key={idx} className="bg-[#FCFAF6] border border-[#E6D5B8]/40 p-6 rounded-2xl flex flex-col hover:border-[#D4AF37] hover:shadow-sm transition-all">
                  <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-[#E6D5B8]/30 flex items-center justify-center text-[#D4AF37] mb-4">
                    {cat.icon}
                  </div>
                  <h4 className="font-bold text-[#302621] mb-3 text-lg">{cat.title}</h4>
                  <ul className="space-y-1.5 mt-auto">
                    {cat.points.map((pt, i) => (
                      <li key={i} className="text-sm text-[#7A695B] flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0 mt-1.5"></span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Keunggulan Formulasi */}
        <section className="py-24 px-6 lg:px-12 bg-[#FCFAF6] border-y border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              <div className="w-full lg:w-5/12">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-[#E6D5B8]/40">
                  <img src="https://images.unsplash.com/photo-1576426863848-c21f53c60b19?q=80&w=800&auto=format&fit=crop" alt="Lab Formulary" className="w-full h-auto object-cover" />
                </div>
              </div>
              <div className="w-full lg:w-7/12">
                <h2 className="text-3xl font-bold text-[#302621] mb-10 text-center md:text-left">Keunggulan Formulasi Kami</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {[
                    { title: "BPOM Ready", desc: "Dibantu hingga proses registrasi BPOM", icon: <ShieldCheck /> },
                    { title: "Halal Ready", desc: "Sertifikasi halal untuk jaminan produk", icon: <CheckCircle2 /> },
                    { title: "Custom Formula", desc: "Formula eksklusif sesuai kebutuhan brand Anda", icon: <FlaskConical /> },
                    { title: "MOQ Fleksibel", desc: "Minimum order yang dapat disesuaikan", icon: <Boxes /> },
                    { title: "R&D Internal", desc: "Tim R&D berpengalaman dan inovatif", icon: <Search /> },
                    { title: "Quality Control Ketat", desc: "Setiap produk melalui uji keamanan & kualitas", icon: <Settings /> }
                  ].map((feat, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="w-10 h-10 shrink-0 rounded-full bg-white border border-[#E6D5B8] flex items-center justify-center text-[#D4AF37] shadow-sm">
                        {feat.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-[#302621] text-base mb-1">{feat.title}</h4>
                        <p className="text-xs text-[#7A695B] leading-relaxed">{feat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Proses Maklon */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-16">Proses Maklon Skincare</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative max-w-5xl mx-auto">
              {[
                { no: "01", title: "Konsultasi", desc: "Diskusi awal ide dan konsep produk" },
                { no: "02", title: "Pembuatan Sample", desc: "Pengembangan dan pengujian sampel formula" },
                { no: "03", title: "Pendaftaran Legalitas", desc: "Pengurusan izin BPOM, Halal, dan HAKI" },
                { no: "04", title: "Desain Packaging", desc: "Pemilihan dan desain kemasan produk" },
                { no: "05", title: "Po Produksi", desc: "Proses produksi skala massal sesuai PO" },
                { no: "06", title: "Pengujian Qualitas", desc: "Uji kontrol kualitas (QC) hasil produksi" },
                { no: "07", title: "Pelunasan Pembayaran", desc: "Penyelesaian administrasi sebelum kirim" },
                { no: "08", title: "Pengiriman Barang", desc: "Produk siap dikirimkan ke lokasi Anda" }
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center w-full mb-4">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-[#D4AF37] shadow-sm flex items-center justify-center text-[#8C6208] font-bold text-sm mb-4">
                    {step.no}
                  </div>
                  <h4 className="font-bold text-[#302621] text-sm mb-2 text-center">{step.title}</h4>
                  <p className="text-[11px] text-[#7A695B] text-center leading-relaxed px-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Produk Unggulan */}
        <section className="py-24 px-6 lg:px-12 bg-[#F9F5EC] border-y border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-12">Produk Unggulan</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-left">
              {[
                { name: "Brightening Serum", desc: "Mencerahkan dan menyamarkan noda hitam", img: "https://images.unsplash.com/photo-1629198688000-71f23e745b6e?q=80&w=300&auto=format&fit=crop" },
                { name: "Acne Serum", desc: "Membantu merawat kulit berjerawat", img: "https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?q=80&w=300&auto=format&fit=crop" },
                { name: "Barrier Moisturizer", desc: "Memperkuat skin barrier dan melembapkan", img: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=300&auto=format&fit=crop" },
                { name: "Hydrating Toner", desc: "Melembapkan dan menyegarkan kulit", img: "https://images.unsplash.com/photo-1556228578-0d85b1a4d571?q=80&w=300&auto=format&fit=crop" },
                { name: "Facial Wash", desc: "Membersihkan kulit dengan lembut", img: "https://images.unsplash.com/photo-1576426863848-c21f53c60b19?q=80&w=300&auto=format&fit=crop" },
                { name: "Sunscreen SPF50", desc: "Melindungi kulit dari sinar UVA & UVB", img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=300&auto=format&fit=crop" }
              ].map((prod, idx) => (
                <div key={idx} className="bg-white p-4 rounded-2xl border border-[#E6D5B8]/40 shadow-sm text-center flex flex-col items-center">
                  <div className="w-24 h-24 mb-4 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                     <img src={prod.img} alt={prod.name} className="h-full object-cover mix-blend-multiply" />
                  </div>
                  <h4 className="font-bold text-[#302621] text-sm mb-2">{prod.name}</h4>
                  <p className="text-[10px] text-[#7A695B] leading-relaxed">{prod.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-[#302621] mb-12 text-center">Pertanyaan yang Sering Diajukan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
              {[
                "Apakah bisa custom formula sesuai kebutuhan brand?",
                "Berapa lama proses pembuatan produk?",
                "Apakah sudah termasuk registrasi BPOM?",
                "Apakah bisa request aroma dan tekstur tertentu?",
                "Berapa minimum order untuk maklon skincare?",
                "Apakah kemasan bisa custom?"
              ].map((q, idx) => (
                <div key={idx} className="bg-[#FCFAF6] border border-[#E6D5B8]/40 p-4 rounded-xl flex justify-between items-center cursor-pointer hover:border-[#D4AF37] transition-all">
                  <span className="font-medium text-sm text-[#4A3B32]">{q}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-[#FCFAF6] border-t border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Siap Menciptakan Produk Skincare Anda?</h2>
            <p className="text-[#7A695B] mb-8">Wujudkan ide dan konsep skincare Anda bersama tim profesional kami.<br/>Konsultasi gratis dan tanpa komitmen.</p>
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 rounded-lg shadow-md border-0 transition-transform hover:-translate-y-1">
              Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
